import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Table, Input, InputNumber, Popconfirm, Form, Tag } from "antd";
import updateDiscount from "../../store/discounts/update/actions";
import deleteDiscount from "../../store/discounts/delete/actions";
import MoneyFormat from "../../helpers/MoneyFormat";

const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
}) => {
    const inputNode =
        inputType === "number" ? <InputNumber /> : <Input />;

    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{
                        margin: 0,
                    }}
                    rules={[
                        {
                            required: true,
                            message: `Por favor completa ${title}!`,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const DiscountsTable = ({ discounts, dataLoading }) => {
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [editingKey, setEditingKey] = useState("");

    const dispatch = useDispatch();

    useEffect(() => {
        setData([...discounts]);
    }, [discounts]);

    const isEditing = (record) => record.key === editingKey;

    const edit = (record) => {
        form.setFieldsValue({
            title: "",
            discount: 0,
            limit_amount: 0,
            tag: "",
            ...record,
        });
        setEditingKey(record.key);
    };

    const cancel = () => {
        setEditingKey("");
    };

    const save = async (key) => {
        try {
            const row = await form.validateFields();
            const newData = [...data];
            const index = newData.findIndex((item) => key === item.key);

            if (index > -1) {
                const item = newData[index];
                newData.splice(index, 1, { ...item, ...row });
                setData(newData);
                setEditingKey("");
                const updatedObject = {
                    ...row,
                    discount_id: item.discount_id,
                };
                dispatch(updateDiscount(updatedObject));
            } else {
                newData.push(row);
                setData(newData);
                setEditingKey("");
            }
        } catch (errInfo) {}
    };

    const handleDeleteDiscount = (discount_id) => {
        dispatch(deleteDiscount(discount_id));
    };

    const columns = [
        {
            title: "Titulo",
            dataIndex: "title",
            key: "title",
            editable: true,
            render: (value) => {
                return <b style={{ fontWeight: "600" }}>{value}</b>;
            },
        },
        {
            title: "Descuento",
            dataIndex: "discount",
            key: "discount",
            editable: true,
            render: (value) => {
                return <b style={{ fontWeight: "600" }}>{value}%</b>;
            },
        },
        {
            title: "Monto Limite",
            dataIndex: "limit_amount",
            key: "limit_amount",
            editable: true,
            render: (value) => {
                return MoneyFormat(value)
            }
        },
        {
            title: "Tag",
            dataIndex: "tag",
            key: "tag",
            editable: true,
        },
        {
            title: "Opciones",
            dataIndex: "operation",
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <button
                            type="button"
                            onClick={() => save(record.key)}
                            style={{
                                marginRight: 8,
                            }}
                        >
                            Guardar
                        </button>
                        <Popconfirm title="Cancelar?" onConfirm={cancel}>
                            <a>Cancelar</a>
                        </Popconfirm>
                    </span>
                ) : (
                    <Fragment>
                        <a
                            disabled={editingKey !== ""}
                            onClick={() => edit(record)}
                        >
                            <strong>Editar</strong>
                        </a>
                        <Popconfirm
                            disabled={editingKey !== ""}
                            title="Estas seguro de eliminar este descuento ?"
                            okText="Eliminar"
                            cancelText="Cancelar"
                            okButtonProps={{ danger: true }}
                            onConfirm={() =>
                                handleDeleteDiscount(record.discount_id)
                            }
                        >
                            <a style={{ marginLeft: "1rem", color: "red" }}>
                                <strong>Eliminar</strong>
                            </a>
                        </Popconfirm>
                    </Fragment>
                );
            },
        },
    ];

    const setInputType = (dataIndex) => {
        if (dataIndex === "discount" || dataIndex === "limit_amount") {
            return "number";
        }
        return "text";
    };

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: setInputType(col.dataIndex),
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    return (
        <Form form={form} component={false}>
            <Table
                components={{
                    body: {
                        cell: EditableCell,
                    },
                }}
                loading={dataLoading}
                bordered
                dataSource={data}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={{
                    onChange: cancel,
                }}
            />
        </Form>
    );
};

export default DiscountsTable;
